/**
 * The colour theme (PLAN-BUREAU § 4 quater): « Clair », « Sombre » or « Système ». It is a per-viewer
 * convenience, kept in this browser only; « Système » (the default) follows prefers-color-scheme,
 * which is what the app did before the choice existed. The resolved theme sits on <html> as
 * `data-theme`, read by Tailwind's `dark:` variant and by the Bureau's palette (index.css).
 *
 * The choice belongs to the Bureau and the Équipe (their sidebar holds it, `useChosenTheme`): the
 * tools keep following the system, as before — their `dark:` rendering was never drawn for a
 * « Sombre » chosen on a light screen.
 */
import { useEffect, useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

export const THEME_PREFERENCES: readonly { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Clair' },
  { value: 'dark', label: 'Sombre' },
  { value: 'system', label: 'Système' },
];

const STORAGE_KEY = 'otto-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export function parseThemePreference(raw: string | null): ThemePreference {
  return raw === 'light' || raw === 'dark' ? raw : 'system';
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): Theme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

function readPreference(): ThemePreference {
  try {
    return parseThemePreference(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Storage blocked (private window, policy): the system decides.
    return 'system';
  }
}

const listeners = new Set<() => void>();
let preference: ThemePreference = 'system';
let resolved: Theme = 'light';
/** The Encre surfaces on screen: while one is, the page follows the viewer's choice. */
let chosenHolders = 0;

function apply() {
  const systemDark = window.matchMedia?.(DARK_QUERY).matches ?? false;
  resolved = resolveTheme(chosenHolders > 0 ? preference : 'system', systemDark);
  document.documentElement.dataset.theme = resolved;
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void) {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

/** Once, before the first render: reads the stored choice and follows the system when asked. */
export function initTheme() {
  preference = readPreference();
  apply();
  // The system's change matters to the tools whatever the choice, and to the Bureau on « Système ».
  window.matchMedia?.(DARK_QUERY).addEventListener('change', apply);
}

/** The page follows the viewer's choice until the returned release is called (the tools: the system). */
export function holdChosenTheme(): () => void {
  chosenHolders += 1;
  apply();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    chosenHolders -= 1;
    apply();
  };
}

/** While mounted (the Bureau's and the Équipe's shell), the page follows the viewer's choice. */
export function useChosenTheme() {
  useEffect(() => holdChosenTheme(), []);
}

export function setThemePreference(next: ThemePreference) {
  preference = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Storage blocked: the choice lasts for this visit.
  }
  apply();
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, () => preference);
}

/** The theme on screen now (« Système » resolved), for what cannot read `data-theme` — a mail's frame. */
export function useResolvedTheme(): Theme {
  return useSyncExternalStore(subscribe, () => resolved);
}
